import { getFormErrorMessage } from "@core/utility/get-error-message";
import { UtilTransportLocationFormRule, UtilTransportLocationFormSchema } from "@factory/form/form.rule";
import { useFormDefaultUtilTransportLocation } from "@factory/form/form-default-util-transport-location.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseUtilTransportLocationModel } from "@factory/model/query-model-util-transport-location";
import { 
  useQyDeleteUtilTransportLocation, 
  useQyGetUtilTransportLocationById, 
  useQyUpdateUtilTransportLocation} from "@factory/query/util-transport-location.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditUtilTransportLocation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultUtilTransportLocation();
  const navigate = useNavigate();
  const { mapUpdateUtilTransportLocation, mapDeleteUtilTransportLocation } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { utilTransportLocationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID UtilTransportLocation
  const handleGetApiSuccess = (data: GetResponseUtilTransportLocationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
  };
  const {
    data: utilTransportLocationResponse,
    isLoading,
    isError: utilTransportLocationError,
  } = useQyGetUtilTransportLocationById(utilTransportLocationId || "", handleGetApiSuccess);
  const utilTransportLocationData = utilTransportLocationResponse?.data?.[0];

  // API UPDATE UtilTransportLocation
  const handleUpdateApiSuccess = () => {
    showSuccess("UtilTransportLocation updated successfully");
  };
  const { mutate: updateUtilTransportLocation, isLoading: isEditLoading } = useQyUpdateUtilTransportLocation(
    handleUpdateApiSuccess
  );

  // API DELETE UtilTransportLocation
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("UtilTransportLocation deleted successfully");
  };
  const { mutate: deleteUtilTransportLocation } = useQyDeleteUtilTransportLocation(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilTransportLocationFormSchema>({
    defaultValues: getDefault(utilTransportLocationData),
    resolver: zodResolver(UtilTransportLocationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: UtilTransportLocationFormSchema) => {
    if (!utilTransportLocationData || !utilTransportLocationData.code) {
      throw new Error("No utilTransportLocation data");
    }

    const formData = mapUpdateUtilTransportLocation(form, utilTransportLocationData.code);
    updateUtilTransportLocation(formData);
  };
  const handleValidateError = (err: FieldErrors<UtilTransportLocationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!utilTransportLocationData || !utilTransportLocationData.code) {
      throw new Error("No utilTransportLocation data found");
    }

    const form = mapDeleteUtilTransportLocation(utilTransportLocationData.code);
    deleteUtilTransportLocation(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    utilTransportLocationId,
    utilTransportLocationData,
    isLoading,
    utilTransportLocationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}